import { getAuthToken, refreshAuthToken } from "auth/authToken";
import { MainContent } from "components/Layout/components/Content";
import { Header, HeaderProps } from "components/Layout/components/Header/Header";
import { MasterLayout } from "components/Layout/components/MasterLayout";
import { SideBar } from "components/Layout/components/SideBar";
import { SupportedApps } from "config/config";
import { useApp } from "hooks/useApp";
import { useEnsureAuthenticated } from "hooks/useEnsureAuthenticated";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const TmsAppEnvironmentProvider = React.lazy(() => import("tmsapp/AppEnvironmentProvider"));
const TmsContent = React.lazy(() => import("tmsapp/TmsContent"));
const TmsMenu = React.lazy(() => import("tmsapp/Menu"));

const PmgAppEnvironmentProvider = React.lazy(() => import("pmgapp/AppEnvironmentProvider"));
const PmgTerminalSendUpdateNotificationModal = React.lazy(() => import("pmgapp/TerminalSendUpdateNotificationModal"));
const PmgTerminalForceUpdateModal = React.lazy(() => import("pmgapp/TerminalForceUpdateModal"));
const PmgTerminalRechargingOperatorsSetupModal = React.lazy(() => import("pmgapp/TerminalRechargingOperatorsSetupModal"));
const PmgTerminalParameterDatHistoryModal = React.lazy(() => import("pmgapp/TerminalParameterDatHistoryModal"));
const PmgOrganizationUnitImportModal = React.lazy(() => import("pmgapp/OrganizationUnitImportModal"));

type TmsAppContainerProps = {
    unauthenticatedHandler: (back: string) => void;
    headerProps: HeaderProps;
};

type ExtraTerminalAction = {
    name: string;
    invoke: (terminalId: string) => void;
};

type ExtraActionBarButton = {
    name: string;
    invoke: () => void;
};

export const TmsPmgAppContainer = ({ unauthenticatedHandler, headerProps }: TmsAppContainerProps) => {
    const navigate = useNavigate();
    const authToken = getAuthToken();
    const ensureAuthenticated = useEnsureAuthenticated();

    const tmsApp = useApp(SupportedApps.tms);
    const pmgApp = useApp(SupportedApps.pmg);

    const [modalElement, setModalElement] = useState<React.ReactNode>(null);

    const hasRole = (_role: string) => {
        return true;
    };

    useEffect(() => {
        refreshAuthToken().catch((e) => {
            // eslint-disable-next-line no-console
            console.error("Auth error", e);
            navigate("/auth");
        });
    }, [navigate]);

    const pmgExtraTerminalActions: ExtraTerminalAction[] = React.useMemo(() => {
        if (!pmgApp.isLoaded()) {
            return [];
        }

        return [
            {
                name: "Send Update Notification",
                invoke: (terminalId: string) => {
                    setModalElement(
                        <PmgTerminalSendUpdateNotificationModal isOpen terminalId={terminalId} onClose={() => setModalElement(null)} />,
                    );
                },
            },
            {
                name: "Force Update via Next Transaction",
                invoke: (terminalId: string) => {
                    setModalElement(<PmgTerminalForceUpdateModal isOpen terminalId={terminalId} onClose={() => setModalElement(null)} />);
                },
            },
            {
                name: "Recharging Operators",
                invoke: (terminalId: string) => {
                    setModalElement(
                        <PmgTerminalRechargingOperatorsSetupModal isOpen terminalId={terminalId} onClose={() => setModalElement(null)} />,
                    );
                },
            },
            {
                name: "Parameter DAT History",
                invoke: (terminalId: string) => {
                    setModalElement(
                        <PmgTerminalParameterDatHistoryModal isOpen terminalId={terminalId} onClose={() => setModalElement(null)} />,
                    );
                },
            },
        ];
    }, [pmgApp]);

    const pmgExtraActionBarButtons: ExtraActionBarButton[] = React.useMemo(() => {
        if (!pmgApp.isLoaded()) {
            return [];
        }

        return [
            {
                name: "Import",
                invoke: () => {
                    setModalElement(<PmgOrganizationUnitImportModal isOpen onClose={() => setModalElement(null)} />);
                },
            },
        ];
    }, [pmgApp]);

    return (
        <>
            {tmsApp.isLoaded() && (
                <TmsAppEnvironmentProvider
                    authToken={authToken}
                    containerVersion={APP_INFO.VERSION}
                    configuration={tmsApp.configuration}
                    extraTerminalActions={pmgExtraTerminalActions}
                    extraActionBarButtons={pmgExtraActionBarButtons}
                    ensureAuthenticated={ensureAuthenticated}
                    hasRole={hasRole}
                    unauthenticatedHandler={unauthenticatedHandler}
                >
                    <MasterLayout>
                        <Header appTitle={headerProps.appTitle} username={headerProps.username} userProfile={headerProps.userProfile} />
                        <SideBar>
                            <TmsMenu />
                        </SideBar>
                        <MainContent id="pageContent">
                            <TmsContent />
                        </MainContent>
                    </MasterLayout>
                </TmsAppEnvironmentProvider>
            )}

            {pmgApp.isLoaded() && modalElement && (
                <PmgAppEnvironmentProvider
                    authToken={authToken}
                    containerVersion={APP_INFO.VERSION}
                    configuration={pmgApp.configuration}
                    extraTerminalActions={[]}
                    pmgExtraActionBarButtons={[]}
                    ensureAuthenticated={ensureAuthenticated}
                    hasRole={hasRole}
                    unauthenticatedHandler={unauthenticatedHandler}
                >
                    {modalElement}
                </PmgAppEnvironmentProvider>
            )}
        </>
    );
};
